import { HttpBaseApi, HttpBaseApiResponse, HttpError, RequestError, RequestOptions } from '@forto-dev/ui-platform-http'
import { isEmpty } from 'lodash-es'
import { logError } from 'utils/logger'

import { AuthStore } from './Auth'
import { UIStore } from './UIStore'
import { getUrlConstructorByPrefix } from './url'

const generatePDF = async <T>(response: Response): Promise<T> => {
  const data = await response.blob()

  return Promise.resolve(data as T)
}

type SerializationStrategy = (params: object) => string
type Options = {
  params?: object
  urlPrefix?: string
  serializationStrategy?: SerializationStrategy
} & ErrorOptions

type ErrorOptions = {
  disableDefaultErrorDisplay?: Boolean
  notifyOnError?: Boolean
}

const defaultOptions: ErrorOptions = {
  disableDefaultErrorDisplay: false,
  notifyOnError: false,
}

const getURLSearchString = (params: object) => {
  const u = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    u.append(key, value)
  })
  return u.toString()
}

export type HttpBaseApiWrapper = {
  get: <T>(relativeUrl: string, options?: Options) => Promise<T>
  post: <T>(relativeUrl: string, body?: object, options?: Options) => Promise<T>
  put: <T>(relativeUrl: string, body?: object, options?: Options) => Promise<T>
  patch: <T>(relativeUrl: string, body?: object, options?: Options) => Promise<T>
  delete: <T>(relativeUrl: string, body?: object, options?: Options) => Promise<T>

  // Extra methods unique to SHIP
  getPDF: <T>(relativeUrl: string, options?: Options) => Promise<T>
  methodWithNoResponse: <T>(relativeUrl: string, body?: object, options?: Options & { method?: string }) => Promise<T>
}

/**
 * Creates an HTTP base API wrapper with error handling and request transformation logic.
 * Wraps default $http functionality and adds default error handlers for Sentry
 */
export const createHttpBaseApiWrapper = (
  onServerError: () => void,
  getAuthStore: () => AuthStore,
  getUIStore: () => UIStore
): HttpBaseApiWrapper => {
  const transformRequestArgs = (relativeUrl: string, options?: Options): [string, RequestOptions, ErrorOptions] => {
    const { params = {}, urlPrefix, serializationStrategy = getURLSearchString } = options || {}
    const search = !isEmpty(params) ? `?${serializationStrategy(params)}` : ''

    let requestOptions: RequestOptions
    const authStore = getAuthStore()
    if (authStore?.state?.token) {
      requestOptions = {
        headers: {
          Authorization: `Bearer ${authStore.state.token}`,
        },
      }
    }
    const url = getUrlConstructorByPrefix(urlPrefix)(relativeUrl) + search
    const errorOptions = {
      disableDefaultErrorDisplay: options?.disableDefaultErrorDisplay || false,
      notifyOnError: options?.notifyOnError || false,
    }
    // @ts-expect-error TS(2454) FIXME: Variable 'requestOptions' is used before being ass... Remove this comment to see the full error message
    return [url, requestOptions, errorOptions]
  }

  // Module-level variables for debouncing 401 errors
  let isHandling401Error = false
  let debounceTimer: ReturnType<typeof setTimeout>
  const DEBOUNCE_401_TIME = 5 * 1000 // Time in milliseconds to debounce 401 errors

  /**
   * Handles errors for all HTTP requests.
   */
  const handleError = (e: Error, options: ErrorOptions) => {
    // Check if the error is an instance of HttpError and has a status of 401 (Unauthorized)
    if (e instanceof HttpError && e.status === 401) {
      // The debounce logic is implemented to address a specific issue where, upon token expiration,
      // multiple simultaneous API calls can fail and all log a 401 error. This results in excessive
      // logging, which the debounce mechanism aims to reduce by logging only the first 401 error and
      // ignoring subsequent ones for a short period.
      if (!isHandling401Error) {
        isHandling401Error = true

        // Clear any existing timer to ensure we set a new one
        clearTimeout(debounceTimer)
        debounceTimer = setTimeout(() => {
          // Reset the flag after the debounce period
          isHandling401Error = false
        }, DEBOUNCE_401_TIME)
      } else {
        // If a 401 error is already being handled (debounced), skip further processing and rethrow the error
        throw e
      }
    }

    if (e instanceof HttpError) {
      if ((e as HttpError).status === 401) {
        getAuthStore().logout()
      }
      if ((e as HttpError).status >= 500 && !options.disableDefaultErrorDisplay) {
        onServerError()
      }
    }
    if (options?.notifyOnError) {
      if ('message' in e) {
        getUIStore().snack.notifyError(e.message)
      }
    }

    logError(e)
    throw e
  }

  const processReponse = <T>(response: HttpBaseApiResponse<T>) => {
    return response.data
  }

  return {
    get: async <T>(relativeUrl: string, options?: Options): Promise<T> => {
      const [url, requestOptions, errorOptions] = transformRequestArgs(relativeUrl, options)
      return HttpBaseApi.get<T>(url, requestOptions)
        .then(processReponse)
        .catch((e) => {
          return handleError(e, errorOptions)
        })
    },
    post: async <T>(relativeUrl: string, body?: object, options: Options = defaultOptions): Promise<T> => {
      const [url, requestOptions, errorOptions] = transformRequestArgs(relativeUrl, options)
      return HttpBaseApi.post<T>(url, body, requestOptions)
        .then(processReponse)
        .catch((e) => {
          return handleError(e, errorOptions)
        })
    },
    put: async <T>(relativeUrl: string, body?: object, options: Options = defaultOptions): Promise<T> => {
      const [url, requestOptions, errorOptions] = transformRequestArgs(relativeUrl, options)
      return HttpBaseApi.put<T>(url, body, requestOptions)
        .then(processReponse)
        .catch((e) => {
          return handleError(e, errorOptions)
        })
    },
    patch: async <T>(relativeUrl: string, body?: object, options: Options = defaultOptions): Promise<T> => {
      const [url, requestOptions, errorOptions] = transformRequestArgs(relativeUrl, options)
      return HttpBaseApi.patch<T>(url, body, requestOptions)
        .then(processReponse)
        .catch((e) => {
          return handleError(e, errorOptions)
        })
    },
    delete: async <T>(relativeUrl: string, body?: object, options: Options = defaultOptions): Promise<T> => {
      const [url, requestOptions, errorOptions] = transformRequestArgs(relativeUrl, options)
      return HttpBaseApi.delete<T>(url, body, requestOptions)
        .then(processReponse)
        .catch((e) => {
          return handleError(e, errorOptions)
        })
    },

    // Extra methods unique to SHIP
    getPDF: async <T>(relativeUrl: string, options?: Options): Promise<T> => {
      const [url, requestOptions] = await transformRequestArgs(relativeUrl, options)
      const requestInit: RequestInit = {
        credentials: 'same-origin',
        headers: {
          ...requestOptions?.headers,
          Accept: 'application/pdf',
          responseType: 'blob',
          'Content-Type': 'application/pdf',
        },
        method: 'get',
      }

      let response: Response
      try {
        response = await fetch(url, requestInit)
      } catch (e) {
        if (e instanceof TypeError) {
          throw new RequestError(e, url)
        } else {
          throw e
        }
      }
      if (!response.ok) {
        let message = 'Server did not respond with a success status'
        let responseData
        // get message from response if available
        try {
          responseData = await response.json()
          message = responseData?.message || message
        } catch (err) {
          if (response.headers && response.headers.get('Content-Type')?.includes('application/json')) {
            console.error(`HttpBaseApi: error parsing failed response as json. URL: ${url}, Error: `, err)
          }
        }
        throw new HttpError(message, response.url, response.status, response.statusText, responseData)
      } else {
        return generatePDF(response)
      }
    },

    // TODO: this is only a temporary solution to fix non-standard use cases.
    // related JIRA task for proper solution: https://forto.atlassian.net/browse/UWG-434
    methodWithNoResponse: async <T>(
      relativeUrl: string,
      body?: object,
      options?: Options & { method?: string }
    ): Promise<T> => {
      const [url, requestOptions] = await transformRequestArgs(relativeUrl, options)
      const requestInit: RequestInit = {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...requestOptions?.headers,
        },
        method: options?.method || 'get',
      }
      if (options?.method?.localeCompare('get', undefined, { sensitivity: 'base' })) {
        requestInit.body = body && JSON.stringify(body)
      }
      let response: Response
      try {
        response = await fetch(url, requestInit)
      } catch (e) {
        if (e instanceof TypeError) {
          throw new RequestError(e, url)
        } else {
          throw e
        }
      }
      if (!response.ok) {
        let message = 'Server did not respond with a success status'
        let responseData
        // get message from response if available
        try {
          responseData = await response.json()
          message = responseData?.message || message
        } catch (err) {
          if (response.headers && response.headers.get('Content-Type')?.includes('application/json')) {
            console.error(`HttpBaseApi: error parsing failed response as json. URL: ${url}, Error: `, err)
          }
        }
        throw new HttpError(message, response.url, response.status, response.statusText, responseData)
      } else {
        return response as T
      }
    },
  }
}
